import { shopProducts } from '@/components/shops';

// Helper function to get a random integer between min and max (inclusive)
function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Helper function to get a random element from an array
function getRandomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

// Helper function to capitalize first letter of each word
function capitalizeWords(str: string): string {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Helper to strip known brands/prefixes from oilType strings
function stripBrandPrefix(oilType: string): string {
  const brands = [
    'liqui moly',
    'castrol',
    'fosser',
    'motul',
    'profi-car',
    'supermax',
  ];
  let type = oilType.trim().toLowerCase();
  for (let brand of brands) {
    if (type.startsWith(brand)) {
      // Remove the brand and extra spaces
      return type.slice(brand.length).trim();
    }
  }
  return oilType.trim();
}

export function generateRecommendations(vehicleData: any) {
  // Default recommendations if we can't determine specifics
  const defaultRecommendations = [
    {
      oilType: 'Fosser Premium LA 5W-30',
      viscosity: '5W-30',
      description: 'High-performance low SAPS engine oil for gasoline and diesel engines.',
      price: '120',
      image: 'https://fosser.de/wp-content/themes/yootheme/cache/ad/fosser-premium-la-5w30-1-ad2306e0.jpeg',
      benefits: [
        'Excellent wear protection',
        'Improved fuel economy',
        'Enhanced engine cleanliness',
        'Suitable for modern engines with emission control systems'
      ],
      volume: '1L, 4L, 5L'
    },
    {
      oilType: 'Castrol EDGE 5W-30',
      viscosity: '5W-30',
      description: 'Advanced full synthetic oil that maximizes engine performance in high-load conditions.',
      price: '150',
      image: '/lovable-uploads/b63c5d30-4a72-4962-b434-361687213022.png',
      benefits: [
        'Superior performance in extreme conditions',
        'Reduces metal-to-metal contact',
        'Maintains strength for longer',
        'Trusted by leading car manufacturers'
      ],
      volume: '1L, 4L, 5L'
    },
    {
      oilType: 'Liqui Moly Top Tec 4200 5W-30',
      viscosity: '5W-30',
      description: 'Low-friction, high-performance oil for modern gasoline and diesel engines.',
      price: '180',
      image: 'https://liquimoly.cloudimg.io/v7/https://www.liqui-moly.com/media/catalog/product/cache/7c4066dd86a6b9217681a7aaaa67e822/8/9/8972_Top_Tec_4200_5W_30_New_Generation_1l_7051.png?w=547.5&h=540&func=bound&sharp=1&org_if_sml=1&force_format=webp%252Coriginal',
      benefits: [
        'Reduces fuel consumption',
        'Excellent cold-start properties',
        'Optimal for vehicles with diesel particulate filters',
        'Maintains engine cleanliness'
      ],
      volume: '1L, 4L, 5L'
    }
  ];

  if (!vehicleData) {
    return defaultRecommendations.map((rec) => {
      const match = shopProducts.find(
        (prod) => prod.name.trim().toLowerCase() === rec.oilType.trim().toLowerCase()
      );
      return {
        ...rec,
        price: match ? match.price.toString() : rec.price || "0",
        image: match ? match.image : rec.image || "",
      };
    });
  }

  // Extract vehicle info
  const { year, make, model, engineType, mileage } = vehicleData;
  const yearNum = parseInt(year, 10);
  const mileageNum = parseInt(mileage, 10) || 50000;

  // NEW: Strict year & hybrid based viscosity selection logic!
  let recommendedViscosities: string[] = [];
  let isHybrid = false;

  if (engineType && typeof engineType === 'string' && engineType.toLowerCase().includes('hybrid')) {
    isHybrid = true;
  } else if (Array.isArray(engineType) && engineType.map(e => String(e).toLowerCase()).includes('hybrid')) {
    isHybrid = true;
  }

  if (isHybrid || yearNum >= 2022) {
    recommendedViscosities = ['0W-20'];
  } else if (yearNum >= 2021 && yearNum <= 2022) {
    recommendedViscosities = ['0W-30'];
  } else if (yearNum >= 2017 && yearNum <= 2020) {
    recommendedViscosities = ['5W-30'];
  } else if (yearNum >= 2013 && yearNum <= 2016) {
    recommendedViscosities = ['5W-40'];
  } else if (yearNum >= 2000 && yearNum <= 2012) {
    recommendedViscosities = ['10W-40'];
  } else {
    recommendedViscosities = ['15W-40'];
  }

  const mainViscosity = recommendedViscosities[0];

  // -------- CUSTOM LOGIC FOR REQUESTED VISCOCITIES --------
  // 15W-40 → Only SuperMax Oilgermany Premium 15W/40
  if (mainViscosity === '15W-40') {
    const product = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'supermax oilgermany premium 15w/40'
    );
    if (product) {
      return [
        {
          oilType: product.name,
          viscosity: mainViscosity,
          description: product.description,
          price: product.price.toString(),
          image: product.image,
          benefits: [
            'Formulated for older/high-mileage engines',
            'Excellent wear protection',
            'Strong oil film at high temperatures',
            'German-made quality'
          ],
          volume: product.volume || '1L, 4L, 5L'
        }
      ];
    }
  }

  // 5W-40 → FOSSER Premium VS 5W-40, MOTUL 300V ROAD RACING 5W-40, Liqui Moly Top Tec 4200 5W-40 (all from products page)
  if (mainViscosity === '5W-40') {
    // 1. FOSSER Premium VS 5W-40
    const fosser = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'fosser premium vs 5w-40'
    );
    // 2. MOTUL 300V ROAD RACING 5W-40 (replace Castrol EDGE 5W-40)
    const motul = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'motul 300v road racing 5w-40'
    );
    // 3. Liqui Moly Top Tec 4200 5W-40 (handle via substring/lowercase, from products page)
    const liqui = shopProducts.find(
      p => p.name.trim().toLowerCase().includes('top tec 4200 5w-40')
    );

    const recs = [];

    if (fosser) {
      recs.push({
        oilType: fosser.name,
        viscosity: mainViscosity,
        description: fosser.description,
        price: fosser.price.toString(),
        image: fosser.image,
        benefits: [
          'Excellent wear protection',
          'Improved fuel economy',
          'Suitable for modern engines',
          'Fosser German technology'
        ],
        volume: fosser.volume || '1L, 4L, 5L'
      });
    }

    if (motul) {
      recs.push({
        oilType: motul.name,
        viscosity: mainViscosity,
        description: motul.description,
        price: motul.price.toString(),
        image: motul.image,
        benefits: [
          'Racing-grade performance',
          'Superior protection under high loads',
          'Motul competition technology',
          'For optimum engine response'
        ],
        volume: motul.volume || '1L, 4L, 5L'
      });
    }

    if (liqui) {
      recs.push({
        oilType: "Liqui Moly Top Tec 4200 5W-40",
        viscosity: mainViscosity,
        description: liqui.description,
        price: liqui.price.toString(),
        image: liqui.image,
        benefits: [
          'High-tech low friction oil',
          'Suitable for gasoline and diesel',
          'Optimal for DPF-equipped vehicles',
          'Made in Germany'
        ],
        volume: liqui.volume || '1L, 4L, 5L'
      });
    }

    // Return all found recommendations for 5W-40 only
    if (recs.length > 0) return recs;
  }

  // 0W-30 → MOTUL 8100 ECO-LITE 0W-30, Top Tec 4310 0W-30 only
  if (mainViscosity === '0W-30') {
    const motul = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'motul 8100 eco-lite 0w-30'
    );
    const topTec = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'top tec 4310 0w-30'
    );

    const recs = [];
    if (motul) {
      recs.push({
        oilType: motul.name,
        viscosity: mainViscosity,
        description: motul.description,
        price: motul.price.toString(),
        image: motul.image,
        benefits: [
          'Fully synthetic for modern engines',
          'Outstanding cold start protection',
          'Motul OEM quality'
        ],
        volume: motul.volume || '1L, 4L, 5L'
      });
    }
    if (topTec) {
      recs.push({
        oilType: topTec.name,
        viscosity: mainViscosity,
        description: topTec.description,
        price: topTec.price.toString(),
        image: topTec.image,
        benefits: [
          'Longlife formulation',
          'Optimal performance for new vehicles',
          'Engineered in Germany'
        ],
        volume: topTec.volume || '1L, 4L, 5L'
      });
    }
    // Return only these recommendations if any present
    if (recs.length > 0) return recs;
  }

  // --- NEW: Show ONLY 3 specific 0W-20 recommendations if main viscosity is 0W-20 ---
  if (mainViscosity === '0W-20') {
    return [
      {
        oilType: 'FOSSER Premium GM 0W-20 (Hybrid Approved)',
        viscosity: '0W-20',
        description: 'Modern fully-synthetic engine oil, specially formulated for GM and hybrid vehicles requiring 0W-20.',
        price: '135',
        image: 'https://fosser.de/wp-content/themes/yootheme/cache/8c/fosser-premium-gm-0w20-1-8cb0ced0.webp',
        benefits: [
          'Ensures optimal fuel economy',
          'Excellent for hybrid and GM vehicles',
          'Provides outstanding engine cleanliness',
          'Meets the latest performance standards'
        ],
        volume: '1L, 4L, 5L'
      },
      {
        oilType: 'Castrol EDGE 0W-20',
        viscosity: '0W-20',
        description: 'Cutting-edge synthetic oil offering maximum performance and protection for modern engines demanding 0W-20.',
        price: '150',
        image: 'https://www.nonpaints.com/media/mf_webp/jpg/media/catalog/product/cache/0c0f220016a16f8e545f2eba1c357a9e/c/a/castrol-edge-0w20-ll-olie-4-liter.webp',
        benefits: [
          'Exceptional strength under pressure',
          'Improves engine efficiency and longevity',
          'Keeps critical engine parts up to 15% cleaner',
          'Recommended by leading car manufacturers'
        ],
        volume: '1L, 4L, 5L'
      },
      {
        oilType: 'Special Tec AA 0W-20',
        viscosity: '0W-20',
        description: 'Premium Liqui Moly low-friction oil, ideal for Asian and US vehicles (Toyota, Honda, Hyundai, etc).',
        price: '155',
        image: 'https://liquimoly.cloudimg.io/v7/https://www.liqui-moly.com/media/catalog/product/cache/7c4066dd86a6b9217681a7aaaa67e822/9/5/95035f8b4306423760a3e29fa0a25c29e710cc68_9705_Special_Tec_AA_0W_20_4l_f6f5.png?w=547.5&h=540&func=bound&sharp=1&org_if_sml=1&force_format=webp%252Coriginal',
        benefits: [
          'Specific for Asian & American (AA) engines',
          'Maximum wear protection & engine longevity',
          'Excellent cold-weather properties',
          'Perfect for the latest Toyota/Honda/Hyundai'
        ],
        volume: '1L, 4L, 5L'
      }
    ];
  }

  // --- NEW: handle 0W-20 special recommendations for modern/hybrid/asian cars ---
  let extraRecommendations: any[] = [];
  if (mainViscosity === '10W-40') {
    extraRecommendations = [
      {
        oilType: 'Castrol Magnatec Engine Oil 10W-40',
        viscosity: '10W-40',
        description: 'Castrol Magnatec Engine Oil 10W-40 provides instant protection from the moment you start.',
        price: '110',
        image: 'https://statuscarcare.com/cdn/shop/files/s-l500-2021-02-05T164939.531.jpg?v=1725195390',
        benefits: [
          'Clingy molecules provide instant protection',
          'Outstanding performance for stop-start driving',
          'Keeps engine parts up to 4x smoother',
          'Helps extend engine life'
        ],
        volume: '1L, 4L, 5L'
      }
    ];
  }

  // Fosser recommendation
  const fosserName = `Fosser Premium ${mainViscosity === '0W-20' && mainViscosity ? 'GM' : (yearNum >= 2010 ? 'LA' : 'VS')} ${mainViscosity}`;
  // Castrol recommendation
  const castrolName = `Castrol EDGE ${mainViscosity}`;
  // Liqui Moly recommendation
  const liquiName = `Liqui Moly ${yearNum >= 2010 ? 'Top Tec 4200' : 'Leichtlauf'} ${mainViscosity}`;

  let recommendations = [
    ...extraRecommendations,
    {
      oilType: fosserName,
      viscosity: mainViscosity,
      description: `High-performance ${yearNum >= 2010 ? 'low SAPS' : 'synthetic'} engine oil for ${engineType || 'gasoline and diesel'} engines.`,
      price: '120',
      image: 'https://fosser.de/wp-content/themes/yootheme/cache/ad/fosser-premium-la-5w30-1-ad2306e0.jpeg',
      benefits: [
        'Excellent wear protection',
        'Improved fuel economy',
        `Ideal for ${make} ${model} engines`,
        mileageNum > 100000 ? 'Suitable for high-mileage vehicles' : 'Enhanced engine cleanliness'
      ],
      volume: '1L, 4L, 5L'
    },
    {
      oilType: castrolName,
      viscosity: mainViscosity,
      description: `Advanced full synthetic oil with Fluid TITANIUM Technology for ${make} ${model} engines.`,
      price: '150',
      image: '/lovable-uploads/b63c5d30-4a72-4962-b434-361687213022.png',
      benefits: [
        'Superior performance in extreme conditions',
        'Reduces metal-to-metal contact',
        `Optimized for ${yearNum >= 2010 ? 'modern' : 'older'} ${make} vehicles`,
        'Trusted by leading car manufacturers'
      ],
      volume: '1L, 4L, 5L'
    },
    {
      oilType: liquiName,
      viscosity: mainViscosity,
      description: `${yearNum >= 2010 ? 'High-tech low-friction' : 'Semi-synthetic'} oil specially formulated for ${engineType || 'gasoline and diesel'} engines.`,
      price: '180',
      image: 'https://liquimoly.cloudimg.io/v7/https://www.liqui-moly.com/media/catalog/product/cache/7c4066dd86a6b9217681a7aaaa67e822/8/9/8972_Top_Tec_4200_5W_30_New_Generation_1l_7051.png?w=547.5&h=540&func=bound&sharp=1&org_if_sml=1&force_format=webp%252Coriginal',
      benefits: [
        'Reduces fuel consumption',
        'Excellent cold-start properties',
        engineType && engineType.toLowerCase().includes('diesel') ? 'Optimal for diesel particulate filters' : 'Enhanced engine performance',
        'Maintains engine cleanliness'
      ],
      volume: '1L, 4L, 5L'
    }
  ];

  // Special 0W-20 removal (existing code)
  if (mainViscosity === '0W-20') {
    recommendations = recommendations.filter(r => {
      const oilName = r.oilType.trim().toLowerCase();
      return oilName !== 'fosser premium la 0w-20'
        && oilName !== 'liqui moly top tec 4200 0w-20'
        && !oilName.startsWith('fosser premium gm 0w-20');
    });
  }

  // NEW LOGIC: Handle 10W-40 case as per user request
  if (mainViscosity === '10W-40') {
    // Remove "Castrol EDGE 10W-40"
    recommendations = recommendations.filter(
      r => r.oilType.trim().toLowerCase() !== 'castrol edge 10w-40'
    );

    // Replace "Fosser Premium VS 10W-40" with "FOSSER Drive TS 10W-40"
    const fosserDriveProduct = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'fosser drive ts 10w-40'
    );
    if (fosserDriveProduct) {
      // Remove "Fosser Premium VS 10W-40" if present (in case it would be in oilType above)
      recommendations = recommendations.filter(
        r => r.oilType.trim().toLowerCase() !== 'fosser premium vs 10w-40'
      );

      // Insert FOSSER Drive TS 10W-40 as the main 10W-40 recommendation right after the extraRecommendations if present, otherwise at start
      const fosserDriveRec = {
        oilType: fosserDriveProduct.name,
        viscosity: '10W-40',
        description: fosserDriveProduct.description,
        price: fosserDriveProduct.price.toString(),
        image: fosserDriveProduct.image,
        benefits: [
          '10W-40 high performance engine oil for modern engines.',
          'Suitable for a wide range of vehicles.',
          'Balanced protection and efficiency.',
          'Fosser German technology'
        ],
        volume: '1L, 4L, 5L'
      };
      // Place this rec after any extraRecommendations included above
      const insertIndex = extraRecommendations.length;
      recommendations.splice(insertIndex, 0, fosserDriveRec);
    }
  }

  // NEW: Handle 5W-30 case - Replace "Fosser Premium VS 5W-30" with "FOSSER Premium LA 5W-30"
  if (mainViscosity === '5W-30') {
    // Remove any "Fosser Premium VS 5W-30" recommendation
    recommendations = recommendations.filter(
      r => r.oilType.trim().toLowerCase() !== 'fosser premium vs 5w-30'
    );

    // Insert FOSSER Premium LA 5W-30 (if not already inserted)
    const laProduct = shopProducts.find(
      p => p.name.trim().toLowerCase() === 'fosser premium la 5w-30'
    );
    if (laProduct) {
      // Remove any previously added variant to avoid duplication
      recommendations = recommendations.filter(
        r => r.oilType.trim().toLowerCase() !== 'fosser premium la 5w-30'
      );
      // Always insert first recommendation spot
      recommendations.unshift({
        oilType: laProduct.name,
        viscosity: '5W-30',
        description: laProduct.description,
        price: laProduct.price.toString(),
        image: laProduct.image,
        benefits: [
          'Excellent wear protection',
          'Improved fuel economy',
          `Ideal for ${make} ${model} engines`,
          mileageNum > 100000 ? 'Suitable for high-mileage vehicles' : 'Enhanced engine cleanliness',
          'Fosser German technology'
        ],
        volume: '1L, 4L, 5L'
      });
    }
  }

  // Update recommendations with shopProducts real prices/images if present
  return recommendations.map((rec) => {
    const normalizedRec = rec.oilType.trim().toLowerCase();
    // Try exact match first
    let match = shopProducts.find(
      (prod) => prod.name.trim().toLowerCase() === normalizedRec
    );

    // Try matches where product name ends with the recommended oil type
    if (!match) {
      match = shopProducts.find(
        (prod) => prod.name.trim().toLowerCase().endsWith(normalizedRec)
      );
    }

    // Try stripping brand from recommendation and match as suffix
    if (!match) {
      const recSuffix = stripBrandPrefix(normalizedRec);
      match = shopProducts.find((prod) => 
        prod.name.trim().toLowerCase().endsWith(recSuffix)
      );
    }

    // Try stripping brand from both and matching
    if (!match) {
      const recSuffix = stripBrandPrefix(normalizedRec);
      match = shopProducts.find((prod) => {
        const prodNameSuffix = stripBrandPrefix(prod.name.trim().toLowerCase());
        return prodNameSuffix === recSuffix;
      });
    }

    // Old fallback: match by inclusion of suffix
    if (!match) {
      const recSuffix = stripBrandPrefix(normalizedRec);
      match = shopProducts.find((prod) =>
        prod.name.trim().toLowerCase().includes(recSuffix)
      );
    }

    let result = {
      ...rec,
      price: match ? match.price.toString() : rec.price || "0",
      image: match ? match.image : rec.image || "",
    };

    // --- IMAGE OVERRIDES for specific recommendations ---
    // Special override for "Liqui Moly Leichtlauf 10W-40"
    if (
      rec.oilType.trim().toLowerCase() === "liqui moly leichtlauf 10w-40" ||
      rec.oilType.trim().toLowerCase() === "leichtlauf 10w-40"
    ) {
      result.image =
        "https://liquimoly.cloudimg.io/v7/https://www.liqui-moly.com/media/catalog/product/cache/7c4066dd86a6b9217681a7aaaa67e822/2/8/28107b9ffaa111d003c2ea6d796e24a3d1408056_9500_Leichtlauf_10W_40_1l_3358.png?w=547.5&h=540&func=bound&sharp=1&org_if_sml=1&force_format=webp%252Coriginal";
    }
    // Special override for "Fosser Premium LA 5W-30"
    if (
      rec.oilType.trim().toLowerCase() === "fosser premium la 5w-30"
    ) {
      result.image =
        "https://fosser.de/wp-content/themes/yootheme/cache/30/fosser-premium-la-5w30-1-30779d48.webp";
    }
    // Special override for "Fosser Premium GM 0W-20 (Hybrid Approved)"
    if (
      rec.oilType.trim().toLowerCase() === "fosser premium gm 0w-20 (hybrid approved)"
    ) {
      result.image =
        "https://fosser.de/wp-content/themes/yootheme/cache/8c/fosser-premium-gm-0w20-1-8cb0ced0.webp";
    }
    // Special override for "Special Tec AA 0W-20"
    if (
      rec.oilType.trim().toLowerCase() === "special tec aa 0w-20"
    ) {
      result.image =
        "https://liquimoly.cloudimg.io/v7/https://www.liqui-moly.com/media/catalog/product/cache/7c4066dd86a6b9217681a7aaaa67e822/9/5/95035f8b4306423760a3e29fa0a25c29e710cc68_9705_Special_Tec_AA_0W_20_4l_f6f5.png?w=547.5&h=540&func=bound&sharp=1&org_if_sml=1&force_format=webp%252Coriginal";
    }

    return result;
  });
}
